import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PasswordInput } from "../../components/ui/password-input";
import { PrimaryButton } from "../../components/vc/Buttons";
import { Building2, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PENDING_KEY = "konexo_pending_connect_code";

/** Phase 7 #2 — business connection deep link: tapping a WhatsApp-shared
 * link like /connect/ABC123 opens straight to this screen, pre-identifying
 * the inviting business — no manual code entry. Works whether the recipient
 * is already logged in, logged out (inline quick login), or brand new
 * (redirected to full signup, code preserved via sessionStorage). */
export default function VCConnectInvite() {
  const { code } = useParams();
  const { user, login } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    apiClient.get(`/connect-preview/${code}`).then((r) => setPreview(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [code]);

  const sendRequest = async () => {
    setSending(true);
    try {
      await apiClient.post("/connection-requests", { code });
      setSent(true);
      toast.success(`Connection request sent to ${preview?.business_name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send request");
    } finally {
      setSending(false);
    }
  };

  const quickLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    const res = await login(email.trim(), password);
    setLoggingIn(false);
    if (!res.ok) toast.error(res.error || "Login failed");
  };

  const goSignup = () => {
    try { sessionStorage.setItem(PENDING_KEY, code); } catch { /* ignore */ }
    window.location.href = "/login";
  };

  return (
    <div className="px-4 pt-10 pb-10 max-w-sm mx-auto space-y-5" data-testid="connect-invite-page">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mx-auto">
          <Building2 className="w-8 h-8" strokeWidth={2.2} />
        </div>
        <h1 className="text-xl font-bold text-slate-900">Business Connection Invite</h1>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400" data-testid="connect-invite-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : !preview ? (
        <div className="text-center py-8 text-sm text-red-600 font-semibold" data-testid="connect-invite-invalid">
          This invite link is invalid or has expired.
        </div>
      ) : (
        <>
          <div data-testid="connect-invite-preview" className="bg-indigo-50 border border-indigo-200 rounded-2xl p-4 flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-indigo-700 mt-0.5 shrink-0" />
            <div className="text-sm">
              <div className="font-bold text-indigo-900">{preview.business_name}</div>
              <div className="text-indigo-800 mt-0.5">wants to connect with you on KonexoCRM.</div>
            </div>
          </div>

          {!user ? (
            <form onSubmit={quickLogin} className="space-y-3" data-testid="connect-invite-login-form">
              <p className="text-xs text-slate-500 text-center">Log in to send the connection request</p>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="connect-login-email" className="h-12 rounded-xl" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="connect-login-password" className="h-12 rounded-xl" />
              </div>
              <PrimaryButton type="submit" loading={loggingIn} data-testid="connect-login-submit" className="w-full">Log in & Connect</PrimaryButton>
              <button type="button" onClick={goSignup} data-testid="connect-invite-signup-link" className="w-full text-center text-xs font-semibold text-indigo-600">
                New to KonexoCRM? Create an account
              </button>
            </form>
          ) : user.role === "employee" ? (
            <p className="text-sm text-center text-slate-500" data-testid="connect-invite-employee-blocked">
              Only your business owner/admin can manage business connections.
            </p>
          ) : sent ? (
            <div className="text-center py-4" data-testid="connect-invite-sent">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-700">Request sent to {preview.business_name}</p>
              <Link to="/businesses" className="text-xs font-semibold text-indigo-600 mt-2 inline-block">View your businesses</Link>
            </div>
          ) : (
            <PrimaryButton onClick={sendRequest} loading={sending} data-testid="connect-invite-send-btn" className="w-full">
              Send Connection Request
            </PrimaryButton>
          )}
        </>
      )}
    </div>
  );
}

export { PENDING_KEY as CONNECT_PENDING_KEY };
